import { Injectable, NgZone, Renderer2, RendererFactory2 } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class TextDirectionService {
  private renderer: Renderer2;
  private observer: MutationObserver | null = null;
  private isRtlMode = false;
  private debounceTimer: any = null;
  private readonly DEBOUNCE_DELAY = 200;

  private readonly TEXT_ELEMENTS =
    'input, textarea, h1, h2, h3, h4, h5, h6, p, span, label';

  constructor(
    rendererFactory: RendererFactory2,
    private zone: NgZone,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  initialize(isRtl: boolean): void {
    this.isRtlMode = isRtl;
    this.applyToExistingElements();
    this.setupMutationObserver();
  }

  updateMode(isRtl: boolean): void {
    if (this.isRtlMode === isRtl) return;

    this.isRtlMode = isRtl;
    this.applyToExistingElements();
  }

  private applyToExistingElements(): void {
    const elements = document.querySelectorAll(this.TEXT_ELEMENTS);
    elements.forEach((el) => this.updateElement(el));
  }

  private updateElement(element: Element): void {
    // ! skip if inside CodeMirror/query editor
    // ! there we do not handle just strings but dom trees
    if (
      element.closest('app-query-editor') ||
      element.closest('quill-editor')
    ) {
      return;
    }

    if (this.isRtlMode) {
      if (element.getAttribute('dir') === 'auto') {
        this.renderer.removeAttribute(element, 'dir');
      }
    } else {
      if (element.getAttribute('dir') !== 'auto') {
        this.renderer.setAttribute(element, 'dir', 'auto');
      }
    }
  }

  private processAddedNodes(nodes: NodeList): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      nodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;

          if (element.matches(this.TEXT_ELEMENTS)) {
            this.updateElement(element);
          }

          const descendants = element.querySelectorAll(this.TEXT_ELEMENTS);
          descendants.forEach((el) => this.updateElement(el));
        }
      });

      if (!this.isRtlMode) {
        const needsDir = document.querySelectorAll(
          `${this.TEXT_ELEMENTS}:not([dir])`,
        );
        if (needsDir.length > 0) {
          needsDir.forEach((el) => this.updateElement(el));
        }
      }

      this.debounceTimer = null;
    }, this.DEBOUNCE_DELAY);
  }

  private setupMutationObserver(): void {
    if (this.observer) {
      this.observer.disconnect();
    }

    function handleMutations(
      this: TextDirectionService,
      mutations: MutationRecord[],
    ) {
      const addedNodes: Node[] = [];

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          addedNodes.push(...Array.from(mutation.addedNodes));
        }
      });

      if (addedNodes.length > 0) {
        this.processAddedNodes(addedNodes as any);
      }
    }

    // * named function can be found in the Perfomance flame charts
    // Outside the zone: a body-wide subtree observer must not re-tick the app on
    // every mutation of a failing render (WAP-1747); it only sets dir attributes.
    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver(handleMutations.bind(this));

      this.observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    });
  }

  destroy(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }
}
